import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import "./BusinessUpdates.css";
import { getAllBusinessUpdates } from "shared/api/function_data";
import { queryKeys } from "shared/lib/queryClient";

/* ─── Constants ─── */

const CARD_FIELDS = [
  { key: "success", title: "Success / Strength" },
  { key: "opportunities", title: "Opportunities & Incident Reporting" },
  { key: "helpNeeded", title: "Help Needed" },
  { key: "nextSteps", title: "Next Steps" },
];

/* ─── Helpers ─── */

const formatTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

/* ─── Sub-components ─── */

const LoadingView = () => (
  <div className="bu-no-data">
    <div className="bu-no-data-content">
      <span className="bu-no-data-icon bu-no-data-icon--spin">⏳</span>
      <h4 className="bu-no-data-title">Loading Business Updates</h4>
      <p className="bu-no-data-text">
        Fetching data from the server, please wait…
      </p>
    </div>
  </div>
);

const ErrorView = ({ message, onRetry }) => (
  <div className="bu-no-data">
    <div className="bu-no-data-content">
      <span className="bu-no-data-icon">⚠️</span>
      <h4 className="bu-no-data-title">Failed to Load Data</h4>
      <p className="bu-no-data-text">{message}</p>
      {onRetry && (
        <button
          className="bu-btn bu-btn--primary"
          onClick={onRetry}
          style={{ marginTop: 16 }}
        >
          Retry
        </button>
      )}
    </div>
  </div>
);

const NoDataView = ({ week }) => (
  <div className="bu-no-data">
    <div className="bu-no-data-content">
      <span className="bu-no-data-icon">📂</span>
      <h4 className="bu-no-data-title">No Data Available</h4>
      <p className="bu-no-data-text">
        No business update information is available for the selected week{" "}
        {week}.
      </p>
    </div>
  </div>
);

const SectionCard = ({ title, content }) => {
  if (!content || !content.trim()) return null;

  return (
    <div className="bu-section-card">
      <div className="bu-section-card-body">
        <h6 className="bu-section-card-title">{title}</h6>
        <div className="bu-section-card-text">
          {content.split("\n").map((paragraph, index) => (
            <p key={index} className="bu-paragraph">
              {paragraph}
            </p>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ── Single segment inside a key group ── */
const SegmentBlock = ({ segment, isCollapsed, onToggleCollapse }) => {
  const filledCount = CARD_FIELDS.filter(
    (f) => segment[f.key] && segment[f.key].trim()
  ).length;

  const hasContent = filledCount > 0;
  if (!hasContent) return null;

  return (
    <div className="bu-function-section">
      <div className="bu-function-header" onClick={onToggleCollapse}>
        <div className="bu-function-header-left">
          <span className="bu-collapse-icon">
            {isCollapsed ? "▶" : "▼"}
          </span>
          <h5 className="bu-function-title">
            {segment.title || "Untitled"}
          </h5>
          <span className="bu-field-count">
            {filledCount} {filledCount === 1 ? "field" : "fields"}
          </span>
        </div>

        {/* ── Updated by badge ── */}
        <div className="bu-function-header-right">
          {segment.updatedBy && (
            <span className="bu-updated-by-badge">
              ✏️ {segment.updatedBy}
              {segment.updatedAt && (
                <span className="bu-updated-by-time">
                  {" "}
                  · {formatTime(segment.updatedAt)}
                </span>
              )}
            </span>
          )}
        </div>
      </div>

      {!isCollapsed && (
        <div className="bu-function-content">
          {CARD_FIELDS.map((field) => (
            <SectionCard
              key={field.key}
              title={field.title}
              content={segment[field.key] || ""}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Key group header that wraps multiple segments ── */
const KeyGroup = ({
  itemKey,
  segments,
  lastUpdated,
  collapsedSegments,
  onToggleSegment,
  isGroupCollapsed,
  onToggleGroup,
}) => {
  const totalSegments = segments.length;
  const totalFields = segments.reduce(
    (acc, seg) =>
      acc +
      CARD_FIELDS.filter((f) => seg[f.key] && seg[f.key].trim()).length,
    0
  );

  return (
    <div className="bu-key-group">
      <div className="bu-key-group-header" onClick={onToggleGroup}>
        <div className="bu-key-group-header-left">
          <span className="bu-collapse-icon">
            {isGroupCollapsed ? "▶" : "▼"}
          </span>
          <h4 className="bu-key-group-title">{itemKey}</h4>
          <span className="bu-key-group-badge">
            {totalSegments} {totalSegments === 1 ? "section" : "sections"}{" "}
            &middot; {totalFields} {totalFields === 1 ? "field" : "fields"}
          </span>
        </div>
        {lastUpdated && (
          <span className="bu-key-group-updated">
            Updated: {lastUpdated}
          </span>
        )}
      </div>

      {!isGroupCollapsed && (
        <div className="bu-key-group-content">
          {segments.map((segment, idx) => {
            const segId = `${itemKey}__${idx}`;
            return (
              <SegmentBlock
                key={segId}
                segment={segment}
                isCollapsed={!!collapsedSegments[segId]}
                onToggleCollapse={() => onToggleSegment(segId)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════
   MAIN COMPONENT — READ ONLY
   ═══════════════════════════════════════════ */

/* ═══════════════════════════════════════════
   MAIN COMPONENT — READ ONLY
   ═══════════════════════════════════════════ */

export default function BusinessUpdates({
  theme,
  functionId,
  week = "N/A",
  year = "N/A",
  label,
}) {
  /* ── Data (cached; invalidated when the upload page saves updates) ── */
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: queryKeys.allBusinessUpdates(functionId, year, week),
    queryFn: () => getAllBusinessUpdates(functionId, year, week),
    enabled: !!functionId,
  });

  const updates = data?.updates || [];
  const totalSegments = data?.total_segments || 0;
  const loading = !!functionId && isLoading;
  const fetchError = error ? error.message || "Could not load business updates." : null;
  const fetchData = refetch; // retry button calls this

  /* ── UI state ── */
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [collapsedSegments, setCollapsedSegments] = useState({});

  /* ── Derived ── */
  const hasData = updates.length > 0;

  const totalFilledFields = updates.reduce((acc, update) => {
    return (
      acc +
      (update.segments || []).reduce(
        (segAcc, seg) =>
          segAcc +
          CARD_FIELDS.filter((f) => seg[f.key] && seg[f.key].trim()).length,
        0
      )
    );
  }, 0);

  /* ── Collapse helpers ── */
  const toggleGroup = (key) =>
    setCollapsedGroups((prev) => ({ ...prev, [key]: !prev[key] }));

  const toggleSegment = (segId) =>
    setCollapsedSegments((prev) => ({ ...prev, [segId]: !prev[segId] }));

  const expandAll = () => {
    setCollapsedGroups({});
    setCollapsedSegments({});
  };

  const collapseAll = () => {
    const groups = {};
    const segs = {};
    updates.forEach((u) => {
      groups[u.key] = true;
      (u.segments || []).forEach((_, idx) => {
        segs[`${u.key}__${idx}`] = true;
      });
    });
    setCollapsedGroups(groups);
    setCollapsedSegments(segs);
  };

  /* ── Check if all updates are just "general" key ── */
  const isAllGeneral =
    updates.length === 1 && updates[0].key === "general";

  /* ── Render ── */
  return (
    <div className="bu-container">
      {/* ───── Header ───── */}
      <div className="bu-header">
        <div className="bu-header-left">
          <h1 className="bu-title">Business Updates</h1>
          <p className="bu-subtitle">
            {label ? `${label} · ` : ""}Week {week}, {year} &middot;{" "}
            {totalSegments} {totalSegments === 1 ? "section" : "sections"}{" "}
            &middot; {totalFilledFields}{" "}
            {totalFilledFields === 1 ? "update" : "updates"}
          </p>
        </div>
        <div className="bu-header-right">
          {!loading && !fetchError && hasData && (
            <>
              <button
                className="bu-btn bu-btn--secondary"
                onClick={expandAll}
              >
                Expand All
              </button>
              <button
                className="bu-btn bu-btn--secondary"
                onClick={collapseAll}
              >
                Collapse All
              </button>
            </>
          )}
        </div>
      </div>

      {/* ───── Content ───── */}
      <div className="bu-sections-wrapper">
        {loading ? (
          <LoadingView />
        ) : fetchError ? (
          <ErrorView message={fetchError} onRetry={fetchData} />
        ) : hasData ? (
          isAllGeneral ? (
            /* ─── "general" key: render segments directly, no group wrapper ─── */
            (updates[0].segments || []).map((segment, idx) => {
              const segId = `general__${idx}`;
              return (
                <SegmentBlock
                  key={segId}
                  segment={segment}
                  isCollapsed={!!collapsedSegments[segId]}
                  onToggleCollapse={() => toggleSegment(segId)}
                />
              );
            })
          ) : (
            /* ─── Multiple keys: render with group wrappers as before ─── */
            updates.map((update) => (
              <KeyGroup
                key={update.key}
                itemKey={update.key}
                segments={update.segments || []}
                lastUpdated={update.last_updated}
                collapsedSegments={collapsedSegments}
                onToggleSegment={toggleSegment}
                isGroupCollapsed={!!collapsedGroups[update.key]}
                onToggleGroup={() => toggleGroup(update.key)}
              />
            ))
          )
        ) : (
          <NoDataView week={week} />
        )}
      </div>

      {/* ───── Footer ───── */}
      <div className="bu-footer-bar">
        Business Updates &middot; Week {week}, {year} &middot;{" "}
        {totalSegments} {totalSegments === 1 ? "section" : "sections"} reported
      </div>
    </div>
  );
}